import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Icons } from './Icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './StandaloneContacts.css';

function generateId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

function exportContacts(contacts, userName) {
  const rows = contacts.map(c => ({
    'Name': c.name,
    'Company / Status': c.company || '',
    'Email': c.email || '',
    'Phone': c.phone || '',
    'Social Link': c.socialLink || ''
  }));
  if (rows.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  const keys = Object.keys(rows[0]);
  ws['!cols'] = keys.map(key => {
    let maxLen = key.length;
    for (const row of rows) { const len = String(row[key] || '').length; if (len > maxLen) maxLen = len; }
    return { wch: Math.min(maxLen + 4, 50) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const date = new Date().toISOString().split('T')[0];
  saveAs(data, `TT_${userName}_Contacts_${date}.xlsx`);
}

export default function StandaloneContacts({ onClose }) {
  const { currentUser, userProfile, setUserProfile } = useAuth();
  const toast = useToast();
  const [contacts, setContacts] = useState(() => userProfile?.standaloneContacts || []);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', company: '', email: '', phone: '', socialLink: '' });
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  function debouncedSave(updated) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!currentUser) return;
      setSaving(true);
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), { standaloneContacts: updated });
        setUserProfile(prev => ({ ...prev, standaloneContacts: updated }));
      } catch (err) { console.error(err); }
      setSaving(false);
    }, 800);
  }

  function addContact(e) {
    e.preventDefault();
    if (!newContact.name.trim()) return;
    const updated = [...contacts, { id: generateId(), ...newContact, name: newContact.name.trim(), company: newContact.company.trim() }];
    setContacts(updated);
    debouncedSave(updated);
    setNewContact({ name: '', company: '', email: '', phone: '', socialLink: '' });
    setShowAdd(false);
    toast('Contact added');
  }

  function updateContact(id, field, value) {
    const updated = contacts.map(c => c.id === id ? { ...c, [field]: value } : c);
    setContacts(updated);
    debouncedSave(updated);
  }

  function deleteContact(id) {
    const updated = contacts.filter(c => c.id !== id);
    setContacts(updated);
    debouncedSave(updated);
    toast('Contact removed');
  }

  const filtered = contacts.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.company.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel slide-up sc-panel" onClick={e => e.stopPropagation()}>
        <div className="panel-header">
          <h2><Icons.Users size={20} /> Only Contacts</h2>
          <button className="panel-close" onClick={onClose}><Icons.X size={18} /></button>
        </div>

        <div className="sc-desc">
          Networking contacts that don't belong to a specific role. Great for people you meet at events, referrals, or general connections.
        </div>

        <div className="sc-toolbar">
          <div className="sc-search">
            <Icons.Search size={14} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..." />
          </div>
          <span className="sc-count">{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</span>
          <button className="sc-export-btn" onClick={() => { exportContacts(contacts, userProfile?.firstName || 'Export'); toast('Contacts exported'); }} title="Export to Excel">
            <Icons.Download size={13} />
          </button>
          {saving && <span className="sc-saving">Saving...</span>}
        </div>

        <div className="sc-table-wrap">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company / Status</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Social Link</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(contact => (
                <tr key={contact.id} className={editingId === contact.id ? 'sc-editing' : ''}>
                  <td>
                    {editingId === contact.id ? (
                      <input value={contact.name} onChange={e => updateContact(contact.id, 'name', e.target.value)} className="sc-cell-input" />
                    ) : (
                      <span className="sc-cell-text" onClick={() => setEditingId(contact.id)}>{contact.name}</span>
                    )}
                  </td>
                  <td>
                    {editingId === contact.id ? (
                      <input value={contact.company} onChange={e => updateContact(contact.id, 'company', e.target.value)} className="sc-cell-input" placeholder="Company / Retired / Switched..." />
                    ) : (
                      <span className="sc-cell-text sc-cell-company" onClick={() => setEditingId(contact.id)}>{contact.company || '—'}</span>
                    )}
                  </td>
                  <td>
                    {editingId === contact.id ? (
                      <input value={contact.email || ''} onChange={e => updateContact(contact.id, 'email', e.target.value)} className="sc-cell-input" placeholder="email@..." />
                    ) : (
                      <span className="sc-cell-text" onClick={() => setEditingId(contact.id)}>
                        {contact.email ? <a href={`mailto:${contact.email}`} onClick={e => e.stopPropagation()}>{contact.email}</a> : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingId === contact.id ? (
                      <input value={contact.phone || ''} onChange={e => updateContact(contact.id, 'phone', e.target.value)} className="sc-cell-input" placeholder="Phone..." />
                    ) : (
                      <span className="sc-cell-text" onClick={() => setEditingId(contact.id)}>{contact.phone || '—'}</span>
                    )}
                  </td>
                  <td>
                    {editingId === contact.id ? (
                      <input value={contact.socialLink || ''} onChange={e => updateContact(contact.id, 'socialLink', e.target.value)} className="sc-cell-input" placeholder="e.g. linkedin.com/in/username" />
                    ) : (
                      <span className="sc-cell-text" onClick={() => setEditingId(contact.id)}>
                        {contact.socialLink ? <a href={/^https?:\/\//i.test(contact.socialLink) ? contact.socialLink : `https://${contact.socialLink}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>Link ↗</a> : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="sc-row-actions">
                      {editingId === contact.id ? (
                        <button className="sc-done-btn" onClick={() => setEditingId(null)}><Icons.Check size={12} /></button>
                      ) : (
                        <button className="sc-edit-btn" onClick={() => setEditingId(contact.id)}><Icons.Edit size={12} /></button>
                      )}
                      <button className="sc-del-btn" onClick={() => deleteContact(contact.id)}><Icons.Trash size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !showAdd && (
                <tr><td colSpan={6} className="sc-empty">{search ? 'No contacts match your search.' : 'No networking contacts yet. Add one below.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {showAdd ? (
          <form className="sc-add-form" onSubmit={addContact}>
            <input autoFocus value={newContact.name} onChange={e => setNewContact({ ...newContact, name: e.target.value })} placeholder="Contact name *" required />
            <input value={newContact.company} onChange={e => setNewContact({ ...newContact, company: e.target.value })} placeholder="Company / Status" />
            <input value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" type="email" />
            <input value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone" />
            <input value={newContact.socialLink} onChange={e => setNewContact({ ...newContact, socialLink: e.target.value })} placeholder="e.g. instagram.com/username" />
            <div className="sc-add-actions">
              <button type="submit" className="sc-add-save" disabled={!newContact.name.trim()}>Add Contact</button>
              <button type="button" className="sc-add-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <button className="sc-add-btn" onClick={() => setShowAdd(true)}>
            <Icons.Plus size={14} /> Add Contact
          </button>
        )}
      </div>
    </div>
  );
}
